import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { SizeGuideToken } from '@sleed/bizcommerce-api';
import { sizeGuidesApi } from '~/lib/api/client';
import { formToToken, type SizeGuideFormValues } from '~/lib/schemas/size-guide';

export const sizeGuideKeys = {
	all: ['size-guides'] as const,
	list: () => [...sizeGuideKeys.all, 'list'] as const,
	detail: (id: number | string) => [...sizeGuideKeys.all, 'detail', id] as const
};

export function useSizeGuidesList() {
	return useQuery<SizeGuideToken[]>({
		queryKey: sizeGuideKeys.list(),
		queryFn: async () => (await sizeGuidesApi.getAll()).data.items ?? []
	});
}

/** Single size guide by id (full record incl. body). `create` → null. */
export function useSizeGuide(id: string) {
	const numericId = Number(id);
	const enabled = Number.isFinite(numericId) && numericId > 0;
	return useQuery<SizeGuideToken | null>({
		queryKey: sizeGuideKeys.detail(id),
		queryFn: async () => (enabled ? (await sizeGuidesApi.getById(numericId)).data : null),
		enabled
	});
}

export function useSizeGuideMutations() {
	const qc = useQueryClient();
	const invalidate = () => qc.invalidateQueries({ queryKey: sizeGuideKeys.all });
	const create = useMutation({
		mutationFn: async (form: SizeGuideFormValues) => (await sizeGuidesApi.insert(formToToken(form))).data,
		onSuccess: invalidate
	});
	const update = useMutation({
		mutationFn: async (form: SizeGuideFormValues) => (await sizeGuidesApi.update(formToToken(form))).data,
		onSuccess: invalidate
	});
	const remove = useMutation({ mutationFn: async (id: number) => (await sizeGuidesApi.remove(id)).data, onSuccess: invalidate });
	return { create, update, remove };
}
